import pricing from './pricing.json';

/**
 * The single source of truth for every advertised price.
 *
 * Change a price by editing `pricing.json` and running `npm run sync:pricing` (which rewrites
 * the standalone SEO page from the same numbers). Nothing else should contain a price literal.
 *
 * NOTE: this is what the app *advertises*. What customers are actually *charged* lives in
 * Chargebee and is not readable from here — the two are verified against each other by
 * completing a test checkout at release time.
 */

function assert(condition, message) {
  if (!condition) {
    throw new Error(`Invalid pricing config: ${message}`);
  }
}

function validate(config) {
  assert(config && typeof config === 'object', 'not an object');
  assert(typeof config.currency === 'string' && config.currency.trim(), 'currency must be a non-empty string');
  assert(typeof config.symbol === 'string' && config.symbol.trim(), 'symbol must be a non-empty string');
  assert(config.plans && typeof config.plans === 'object', 'plans must be an object');

  for (const key of ['monthly', 'annual_upfront']) {
    const plan = config.plans[key];
    assert(plan && typeof plan === 'object', `plans.${key} is missing`);
    assert(Number.isFinite(plan.amount) && plan.amount > 0, `plans.${key}.amount must be a positive number`);
  }

  const { termMonths } = config.plans.annual_upfront;
  assert(
    Number.isInteger(termMonths) && termMonths > 0,
    'plans.annual_upfront.termMonths must be a positive integer'
  );

  return config;
}

const CONFIG = validate(pricing);

export const CURRENCY = CONFIG.currency;
export const SYMBOL = CONFIG.symbol;
export const MONTHLY_AMOUNT = CONFIG.plans.monthly.amount;
export const ANNUAL_AMOUNT = CONFIG.plans.annual_upfront.amount;
export const ANNUAL_TERM_MONTHS = CONFIG.plans.annual_upfront.termMonths;

/** `9.99` → `$9.99`, `99` → `$99`. Whole amounts drop the redundant `.00`. */
export function formatAmount(amount) {
  const isWhole = Number.isInteger(amount);
  return `${SYMBOL}${isWhole ? amount : amount.toFixed(2)}`;
}

/** `9.99` → `AUD $9.99` */
export function formatWithCurrency(amount) {
  return `${CURRENCY} ${formatAmount(amount)}`;
}

export const MONTHLY_PRICE_LABEL = `${formatWithCurrency(MONTHLY_AMOUNT)}/month`;
export const ANNUAL_PRICE_LABEL = `${formatWithCurrency(ANNUAL_AMOUNT)} upfront`;

/** Short forms for the standalone SEO page. */
export const MONTHLY_SHORT = formatAmount(MONTHLY_AMOUNT);
export const ANNUAL_SHORT = formatAmount(ANNUAL_AMOUNT);
export const MONTHLY_PER_MO = `${CURRENCY} ${MONTHLY_SHORT}/mo`;
export const ANNUAL_PER_YR = `${CURRENCY} ${ANNUAL_SHORT}/yr`;

/** JSON-LD `Offer.price` wants a plain decimal string. */
export const MONTHLY_JSONLD = MONTHLY_AMOUNT.toFixed(2);
export const ANNUAL_JSONLD = ANNUAL_AMOUNT.toFixed(2);

/** Discount of the annual plan against 12 months at the monthly rate. */
export const ANNUAL_SAVINGS_PERCENT = Math.round(
  (1 - ANNUAL_AMOUNT / (MONTHLY_AMOUNT * ANNUAL_TERM_MONTHS)) * 100
);

export const PRICING_COPY =
  `After the free trial, choose either ${MONTHLY_PRICE_LABEL} or ` +
  `${ANNUAL_PRICE_LABEL} for ${ANNUAL_TERM_MONTHS} months.`;

export default CONFIG;
